import { Copy, Download, Remove } from '@app/components/various/Icons';
import { getIsOrderEditable } from '@app/logic/Orders';
import { isDefined } from '@app/utils/is';
import { Order } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAmplitude } from '../../analytics/useAmplitude';

interface Props {
  order: Order.Essentials;
  onDelete?: (order: Order.Essentials) => void;
  onCopyToNewSelection: (order: Order.Essentials) => void;
  onLinesheetDownload: (order: Order.Essentials) => void;
}

export const useOrderListOptions = ({ onDelete, order, onCopyToNewSelection, onLinesheetDownload }: Props) => {
  const { t } = useTranslation(['common', 'confirmationConfig', 'selections']);
  const { trackEvent } = useAmplitude();

  const handleDownloadLinesheet = React.useCallback(() => {
    const isOrderEditable = getIsOrderEditable(order.status);

    trackEvent({ name: isOrderEditable ? 'selectionsTable.link.downloadLinesheet.click' : 'ordersTable.link.downloadLinesheet.click' });
    onLinesheetDownload(order);
  }, [order, trackEvent, onLinesheetDownload]);

  const handleCopyToNewSelection = React.useCallback(() => {
    onCopyToNewSelection(order);
  }, [order, onCopyToNewSelection]);

  const deleteOption = React.useMemo(() => {
    if (!isDefined(onDelete)) {
      return [];
    }

    return [
      {
        icon: Remove,
        key: 'deleteSelection',
        label: t('selections:delete_this_selection'),
        onClick: () => onDelete(order),
      },
    ];
  }, [onDelete, order, t]);

  return React.useMemo(() => {
    return [
      {
        icon: Copy,
        key: 'copyToNewSelection',
        label: t('selections:copy_to_new_selection'),
        onClick: handleCopyToNewSelection,
      },
      {
        icon: Download,
        key: 'downloadLinesheet',
        label: t('common:download_linesheet'),
        onClick: handleDownloadLinesheet,
      },
      ...deleteOption,
    ];
  }, [deleteOption, t, handleCopyToNewSelection, handleDownloadLinesheet]);
};
